<template>
  <div>
    <v-dialog
      v-model="show"
      content-class="crud-dialog"
      persistent
      max-width="400px"
    >
      <v-card v-if="!deleteCheck" class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 v-if="isEditForm" class="text-h5">Update charity</h2>
              <h2 v-else class="text-h5">Add new charity</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close"
                color="black"
                @click.stop="closeDialog"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="crud-card-text">
          <div v-if="showExistingCharities" class="text-center">
            <p>Select an existing charity or add a new charity</p>
            <div
              v-for="(existingCharity, index) in existingCharities"
              :key="index"
            >
              <v-btn
                block
                depressed
                class="existing-item-btn"
                :disabled="charityDisabled(existingCharity)"
                @click="selectExistingCharity(existingCharity)"
              >
                <div class="existing-item-text">
                  <span class="existing-item-name">{{
                    existingCharity.displayName
                  }}</span>
                  <span v-if="existingCharity.disabledMessage">
                    <br />
                    <span class="existing-item-extra-info">{{
                      existingCharity.disabledMessage
                    }}</span>
                  </span>
                </div>
                <div>
                  <v-icon class="plus-icon" color="primary">{{
                    icons.mdiPlusCircle
                  }}</v-icon>
                </div>
              </v-btn>
            </div>
          </div>

          <h3 v-if="showExistingCharities" class="ml-1 mt-4 mb-6">
            Add a charity
          </h3>
          <ValidationObserver ref="observer">
            <v-form class="modal-form">
              <ValidationProvider
                id="charity_name"
                v-slot="{ errors }"
                name="charity_name"
                rules="required"
              >
                <TextField
                  v-model.trim="charity.name"
                  :label="'Charity name*'"
                  :errors="errors"
                  :name="'charity_name'"
                />
              </ValidationProvider>
              <TextField
                v-model.trim="charity.registrationNumber"
                :label="'Charity registration number'"
                :name="'charity_name'"
              />
            </v-form>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions class="crud-card-actions">
          <v-row no-gutters>
            <v-col v-if="isEditForm" cols="12">
              <div class="button-container">
                <GradientButton
                  :title="'Save'"
                  :small="true"
                  :callout="'right'"
                  @gradientButtonClicked="save"
                />
                <GradientButton
                  :title="'Remove Charity'"
                  :small="true"
                  :fill="false"
                  @gradientButtonClicked="deleteCheck = true"
                />
              </div>
            </v-col>

            <v-col v-else cols="12">
              <div class="button-container">
                <GradientButton
                  :title="'Save And Continue'"
                  :small="true"
                  :callout="'right'"
                  :disabled="buttonLoading"
                  @gradientButtonClicked="save"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>

      <DeleteDialog
        v-if="deleteCheck"
        :content="deleteDialogContent"
        :hide-delete-button="hideDeleteButton"
        @delete="deleteCharity"
        @dialogClosed="closeDialog"
      />
    </v-dialog>

    <ErrorSnackbar v-model="showError" />
  </div>
</template>

<script>
  import {
    CREATE_CHARITY_MUTATION,
    UPDATE_CHARITY_MUTATION,
    DELETE_CHARITY_MUTATION,
  } from '../../constants/graphql/charities';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import DeleteDialog from '../common/DeleteDialog.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';

  export default {
    name: 'CharityDialog',
    components: {
      DeleteDialog,
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      value: Boolean,
      charityToUpdate: Object,
      existingCharities: Array,
    },
    data() {
      return {
        charity: this.charityToUpdate || {},
        deleteCheck: false,
      };
    },
    computed: {
      show: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
      deleteDialogContent() {
        return {
          title: 'Remove ' + this.charity.name,
          text: this.deleteText,
          buttonText: 'Yes, Remove ' + this.toCapitalCase(this.charity.name),
        };
      },
      showExistingCharities() {
        return this.existingCharities && this.existingCharities.length > 0;
      },
      isEditForm() {
        return this.charity && this.charity.id;
      },
      deleteText() {
        if (this.charityHasRemainderOfEstateSpecificPercentage) {
          return (
            'You have left a part of the remainder of your estate to ' +
            this.charity.name +
            ', please amend the Remainder of Estate section before deleting ' +
            this.charity.name
          );
        } else {
          return (
            'Are you sure you want to remove <b>' +
            this.charity.name +
            '</b>?<br><br><b>Warning:</b> Any donations left to this charity will also be deleted.'
          );
        }
      },
      hideDeleteButton() {
        return this.charityHasRemainderOfEstateSpecificPercentage
          ? true
          : false;
      },
      charityHasRemainderOfEstateSpecificPercentage() {
        return (
          this.charity.distributionSpecificPercentage ||
          this.charity.alternativeDistributionSpecificPercentage ||
          this.charity.distributionSpecificPercentageAlternatives?.length > 0 ||
          this.charity.distributionSpecificPercentageAlternativeBeneficiaries
            ?.length > 0
        );
      },
    },
    watch: {
      value: function (dialogOpen) {
        if (dialogOpen && this.charityToUpdate) {
          // using native JSON functions removes reactivity
          // so we can clone an object without mutating the original source
          this.charity = JSON.parse(JSON.stringify(this.charityToUpdate));
        }
      },
    },
    methods: {
      save() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            if (this.charity.id) {
              this.updateCharity();
            } else {
              this.createCharity();
            }
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      createCharity() {
        const charityInputDetails = {
          testatorId: this.testatorId,
          name: this.charity.name,
          registrationNumber: this.charity.registrationNumber,
        };

        this.$apollo
          .mutate({
            mutation: CREATE_CHARITY_MUTATION,
            variables: {
              charityInputDetails,
            },
            update: (cache, { data: { createCharity } }) => {
              this.updateCacheAfterCreateCharity(cache, createCharity);
            },
          })
          .then(response => {
            this.closeDialog();
            this.$emit('charityCreated', response.data.createCharity);
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterCreateCharity(cache, createCharity) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        data.testator.charities.push(createCharity);

        cache.writeQuery({ ...query, data: data });
      },
      updateCharity() {
        const charityInputDetails = {
          name: this.charity.name,
          registrationNumber: this.charity.registrationNumber,
        };
        const id = this.charity.id;

        this.$apollo
          .mutate({
            mutation: UPDATE_CHARITY_MUTATION,
            variables: {
              id,
              charityInputDetails,
            },
            update: (cache, { data: { updateCharity } }) => {
              this.updateCacheAfterUpdateCharity(cache, updateCharity);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterUpdateCharity(cache, updateCharity) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var charity = data.testator.charities.find(
          charity => charity.id == updateCharity.id
        );
        var charity = updateCharity;

        cache.writeQuery({ ...query, data: data });
      },
      deleteCharity() {
        const id = this.charity.id;

        this.$apollo
          .mutate({
            mutation: DELETE_CHARITY_MUTATION,
            variables: {
              id,
            },
            update: (cache, { data: { deleteCharity } }) => {
              this.updateCacheAfterDeleteCharity(cache, deleteCharity);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterDeleteCharity(cache, deleteCharity) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var charities = data.testator.charities;
        var index = charities.findIndex(
          charity => charity.id == deleteCharity.id
        );
        charities.splice(index, 1);

        cache.writeQuery({ ...query, data: data });
      },
      closeDialog() {
        this.show = false;
        setTimeout(() => this.clearDialog(), 300);
      },
      clearDialog() {
        this.buttonLoading = false;
        this.charity = {};
        if (this.$refs.observer) {
          this.$refs.observer.reset();
        }
        this.deleteCheck = false;
      },
      selectExistingCharity(charity) {
        this.$emit('existingCharitySelected', charity);
        this.closeDialog();
      },
      charityDisabled(charity) {
        return charity.disabledMessage ? true : false;
      },
      toCapitalCase(str) {
        return str
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      },
    },
  };
</script>

<style lang="scss" scoped>
  .button-container {
    margin-bottom: 22px;
    * {
      margin-right: 15px;
    }
  }
</style>
